import React from "react";

import { IGCPBigQueryJob } from "../../../../Models/IGCPBigQueryJob";
import { IRetailMatchJob } from "../../../../Models/IRetailMatch";
import { App } from "../../Enums/App";
import { NotificationType } from "../../Enums/NotificationType";
import { ReportProgressStage, ReportProgressStageName } from "../../Enums/ReportProgressStage";
import { IReportProgress, ReportProgress } from "../../Models/IReportProgress";
import { INotification } from "../../Models/Notification";
import { regularExpressions } from '../../Constants/RegularExpression';

export const getPendingReportProgress = (): IReportProgress => {
    return new ReportProgress(
        ReportProgressStage.PENDING,
        (
            <span>Pending</span>
        ), ReportProgressStageName.PENDING);
};

export const getSubmittedReportProgress = (): IReportProgress => {
    return new ReportProgress(
        ReportProgressStage.SUBMITTED,
        (
            <span>Submitted</span>
        ), ReportProgressStageName.SUBMITTED);
};

export const getCachingReportProgress = (): IReportProgress => {
    return new ReportProgress(
        ReportProgressStage.CACHING,
        (
            <span>Caching</span>
        ), ReportProgressStageName.CACHING);
};

export const getCompleteReportProgress = (): IReportProgress => {
    return new ReportProgress(
        ReportProgressStage.COMPLETE,
        (
            <span>Complete</span>
        ), ReportProgressStageName.COMPLETE);
};

export const getNextReportProgress = (progress: number, locationName: string): IReportProgress => {
    /** set the initial progress, override it if ok */
    const sp = getPendingReportProgress();
    let nextProgress = sp.progress;
    let nextVerbiage = sp.verbiage;
    let nextStageName = sp.stageName;

    switch (progress){
        case ReportProgressStage.PENDING: {
            const p = getSubmittedReportProgress();
            nextProgress = p.progress;
            nextVerbiage = p.verbiage;
            nextStageName = p.stageName;
            break;
        }
        case ReportProgressStage.SUBMITTED: {
            nextProgress = ReportProgressStage.TWO;
            nextVerbiage = (
                <span>Identifying and quantifying mobile device signals observed at selected brand(s) locations</span>
            );
            nextStageName = ReportProgressStageName.SUBMITTED;
            break;
        }
        case ReportProgressStage.TWO: {
            nextProgress = ReportProgressStage.THREE;
            nextVerbiage = (
                <span>Identifying home locations of mobile devices and calculating location trade areas</span>
            );
            nextStageName = ReportProgressStageName.RUNNING;
            break;
        }
        case ReportProgressStage.THREE: {
            nextProgress = ReportProgressStage.FOUR;
            nextVerbiage = (
                <span>Appending household level attributes to develop customer profiles</span>
            );
            nextStageName = ReportProgressStageName.RUNNING;
            break;
        }
        case ReportProgressStage.FOUR: {
            nextProgress = ReportProgressStage.FIVE;
            nextVerbiage = (
                <span>Assessing trade area household compositions for selected brand(s) locations</span>
            );
            nextStageName = ReportProgressStageName.RUNNING;
            break;
        }
        case ReportProgressStage.FIVE: {
            nextProgress = ReportProgressStage.SIX;
            nextVerbiage = (
                <span>Assessing trade area household composition for analysis <b>{locationName?.replace(regularExpressions.UsaRegex, "")}</b></span>
            );
            nextStageName = ReportProgressStageName.RUNNING;
            break;
        }
        case ReportProgressStage.SIX: {
            nextProgress = ReportProgressStage.SEVEN;
            nextVerbiage = (
                <span>Comparing analysis trade area to brand trade areas to generate key metrics</span>
            );
            nextStageName = ReportProgressStageName.RUNNING;
            break;
        }
        case ReportProgressStage.SEVEN: {
            nextProgress = ReportProgressStage.CACHING;
            nextVerbiage = (
                <span>Caching</span>
            );
            nextStageName = ReportProgressStageName.CACHING;
            break;
        }
        case ReportProgressStage.CACHING: {
            const np = getCachingReportProgress();
            nextProgress = np.progress;
            nextVerbiage = np.verbiage;
            nextStageName = np.stageName;
            break;
        }
        case ReportProgressStage.COMPLETE: {
            const np = getCompleteReportProgress();
            nextProgress = np.progress;
            nextVerbiage = np.verbiage;
            nextStageName = np.stageName;
            break;
        }
    }
    return new ReportProgress(nextProgress, nextVerbiage, nextStageName);
};

export const getReportProgressTimerValue = (progress: number): number => {
    /** based on the progress passed in, will return the value for the timer function - IN MILLISECONDS */
    let numSeconds = 5;
    switch (progress){
        case ReportProgressStage.PENDING:
        case ReportProgressStage.SUBMITTED:
        case ReportProgressStage.TWO:
        case ReportProgressStage.THREE:
        case ReportProgressStage.COMPLETE: {
            numSeconds = 5;
            break;
        }
        case ReportProgressStage.FOUR:
        case ReportProgressStage.SIX:
        case ReportProgressStage.SEVEN: {
            numSeconds = 6;
            break;
        }
        case ReportProgressStage.FIVE: {
            numSeconds = 9;
            break;
        }
        case ReportProgressStage.CACHING: {
            numSeconds = 10;
            break;
        }
    }
    return numSeconds * 1000;// example - converts 5 to 5000 (5 seconds, or 5000 milliseconds)
};

export const reportIsPending = (
    associatedNotifications: INotification[],
    app: App
): boolean => {

    const running: boolean = reportIsRunning(associatedNotifications, app);
    const caching: boolean = reportIsCaching(associatedNotifications, app);
    const complete: boolean = reportIsComplete(associatedNotifications, app);
    if (running || caching || complete) {
        return false;
    }

    return associatedNotifications.find((n: INotification) => {
        switch (app){
            case App.Match: {
                return n.notificationType === NotificationType.MatchReportPending;
            }
            case App.Mobilytics: {
                return n.notificationType === NotificationType.MobilyticsReportPending;
            }
        }
    }) !== undefined;
};

export const reportIsRunning = (
    associatedNotifications: INotification[],
    app: App
): boolean => {

    const caching: boolean = reportIsCaching(associatedNotifications, app);
    const complete: boolean = reportIsComplete(associatedNotifications, app);
    if (caching || complete) {
        return false;
    }

    return associatedNotifications.find((n: INotification) => {
        switch (app){
            case App.Match: {
                return n.notificationType === NotificationType.MatchReportRunning;
            }
            case App.Mobilytics: {
                return n.notificationType === NotificationType.MobilyticsReportRunning;
            }
        }
    }) !== undefined;
};

export const reportIsCaching = (
    associatedNotifications: INotification[],
    app: App
): boolean => {

    const complete: boolean = reportIsComplete(associatedNotifications, app);
    if (complete) {
        return false;
    }

    return associatedNotifications.find((n: INotification) => {
        switch (app){
            case App.Match: {
                return n.notificationType === NotificationType.MatchReportCaching;
            }
            case App.Mobilytics: {
                return n.notificationType === NotificationType.MobilyticsReportCaching;
            }
        }
    }) !== undefined;
};

export const reportIsComplete = (
    associatedNotifications: INotification[],
    app: App
): boolean => {
    return associatedNotifications.find((n: INotification) => {
        switch (app){
            case App.Match: {
                return n.notificationType === NotificationType.MatchReportComplete
                || n.notificationType === NotificationType.MatchReportError;
            }
            case App.Mobilytics: {
                return n.notificationType === NotificationType.MobilyticsReportComplete
                || n.notificationType === NotificationType.MobilyticsReportError;
            }
        }
    }) !== undefined;
};

export const getMostRecentReport = (
    app: App,
    matchReport: IRetailMatchJob,
    mobilyticsReport: IGCPBigQueryJob[]
): IRetailMatchJob | IGCPBigQueryJob => {
    let report = null;
    switch (app){
        case App.Match: {
            report = matchReport;
            break;
        }
        case App.Mobilytics: {
            /** Mobilytics reports can have more than one job (i.e. Benchmark Reports).
             * For this use case, all we need is the first one to check against.
             */
            report = mobilyticsReport[0];
            break;
        }
    }
    return report;
};

export const getAssociatedNotifications = (
    report: IGCPBigQueryJob | IRetailMatchJob,
    notifications: INotification[]
): INotification[] => {
    return notifications.filter((n: INotification) => {
        return n.linkId === report?.jobId;
    });
};
